export const RECOVERY_LIMIT = 60 * 60 * 1000;
export const RECOVERY_DELAYS = [5000, 10000, 30000, 60000, 120000];
const listeners = new Set();
export const subscribeMessagingNetworkFailure = listener => { listeners.add(listener); return () => listeners.delete(listener); };
export function reportMessagingNetworkFailure(error) {
  if (error?.networkFailure) listeners.forEach(listener => listener());
}

// One bounded retry owner per authenticated client, independent of renders.
export function createConnectionRecovery({ recover, onChange, now = Date.now }) {
  let state = 'online', phase = null, lostAt = null, step = 0, timer, deadline, socketTimer, revision = 0, busy = false, disposed = false;
  const emit = (next, nextPhase = null) => { state = next; phase = nextPhase; onChange({ state, phase }); };
  const clear = () => { clearTimeout(timer); clearTimeout(deadline); clearTimeout(socketTimer); };
  const exhausted = () => lostAt != null && now() - lostAt >= RECOVERY_LIMIT;
  const giveUp = () => { clear(); revision++; busy = false; emit('gave_up'); };
  const schedule = () => {
    clearTimeout(timer);
    if (disposed || state === 'gave_up') return;
    if (exhausted()) { giveUp(); return; }
    timer = setTimeout(() => attempt(false), RECOVERY_DELAYS[Math.min(step++, RECOVERY_DELAYS.length - 1)]);
  };
  const lost = () => {
    if (disposed || state === 'gave_up') return;
    if (lostAt == null) {
      lostAt = now(); step = 0;
      deadline = setTimeout(giveUp, RECOVERY_LIMIT);
      emit('offline'); schedule();
    } else if (!busy) emit('offline');
  };
  async function attempt(manual) {
    if (disposed || busy || (!manual && (state === 'gave_up' || exhausted()))) {
      if (!manual && exhausted() && state !== 'gave_up') giveUp();
      return false;
    }
    clearTimeout(timer);
    const wasGaveUp = state === 'gave_up', ticket = ++revision;
    busy = true;
    emit('restored', 'syncing');
    let ok = false;
    try { ok = await recover() !== false; } catch (_) { ok = false; }
    if (disposed || ticket !== revision) return false;
    busy = false;
    if (ok) {
      clear(); lostAt = null; step = 0;
      emit('restored', 'up_to_date');
      timer = setTimeout(() => emit('online'), 2000);
    } else if (wasGaveUp || exhausted()) giveUp();
    else {
      if (lostAt == null) lost();
      emit('reconnecting'); schedule();
    }
    return ok;
  }
  return {
    lost,
    wake: () => { if (state !== 'gave_up' && lostAt != null) { clearTimeout(timer); timer = setTimeout(() => attempt(false), 5000); } },
    reconcile: () => { if (state !== 'gave_up') { clearTimeout(timer); timer = setTimeout(() => attempt(false), 5000); } },
    socket: connected => {
      clearTimeout(socketTimer);
      if (!connected) socketTimer = setTimeout(lost, 10000);
      else if (lostAt != null) { clearTimeout(timer); timer = setTimeout(() => attempt(false), 5000); }
    },
    retryNow: () => attempt(true),
    snapshot: () => ({ state, phase }),
    dispose: () => { disposed = true; revision++; clear(); },
  };
}
